import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"

type AddMcpDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (config: {
    label: string
    url: string
    allowed_tools?: string[] | null
    require_approval: string
    enabled: boolean
  }) => Promise<void>
  isPending: boolean
}

export function AddMcpDialog({ isOpen, onOpenChange, onSubmit, isPending }: AddMcpDialogProps) {
  const { t } = useTranslation()

  const [label, setLabel] = useState("")
  const [url, setUrl] = useState("")
  const [allowedTools, setAllowedTools] = useState("")
  const [requireApproval, setRequireApproval] = useState("never")

  const [errors, setErrors] = useState<{ label?: string; url?: string }>({})

  useEffect(() => {
    if (!isOpen) {
      setLabel("")
      setUrl("")
      setAllowedTools("")
      setRequireApproval("never")
      setErrors({})
    }
  }, [isOpen])

  const validate = () => {
    const newErrors: { label?: string; url?: string } = {}

    if (!label.trim()) {
      newErrors.label = t("adminDashboard.mcps.addDialog.errors.labelRequired", "Label is required")
    }

    if (!url.trim()) {
      newErrors.url = t("adminDashboard.mcps.addDialog.errors.urlRequired", "URL is required")
    } else {
      try {
        new URL(url)
      } catch {
        newErrors.url = t("adminDashboard.mcps.addDialog.errors.urlInvalid", "Invalid URL format")
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validate()) return

    const allowedToolsList = allowedTools
      .split(",")
      .map((tool) => tool.trim())
      .filter((tool) => tool.length > 0)

    await onSubmit({
      label: label.trim(),
      url: url.trim(),
      allowed_tools: allowedToolsList.length > 0 ? allowedToolsList : null,
      require_approval: requireApproval,
      enabled: true,
    })

    onOpenChange(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("adminDashboard.mcps.addDialog.title", "Add MCP Server")}</DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.mcps.addDialog.description",
              "Configure a new MCP server connection for the agent."
            )}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="label">
              {t("adminDashboard.mcps.addDialog.labelField", "Label")}
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="label"
              value={label}
              placeholder={t(
                "adminDashboard.mcps.addDialog.labelPlaceholder",
                "e.g., MicrosoftDocs"
              )}
              onChange={(event) => setLabel(event.target.value)}
            />
            {errors.label && <p className="text-destructive text-sm">{errors.label}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="url">
              {t("adminDashboard.mcps.addDialog.urlField", "URL")}
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="url"
              type="url"
              value={url}
              placeholder={t(
                "adminDashboard.mcps.addDialog.urlPlaceholder",
                "https://api.example.com/mcp"
              )}
              onChange={(event) => setUrl(event.target.value)}
            />
            {errors.url && <p className="text-destructive text-sm">{errors.url}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="allowedTools">
              {t("adminDashboard.mcps.addDialog.allowedToolsField", "Allowed Tools")}
            </Label>
            <Input
              id="allowedTools"
              value={allowedTools}
              placeholder={t(
                "adminDashboard.mcps.addDialog.allowedToolsPlaceholder",
                "tool1, tool2 (leave empty for all)"
              )}
              onChange={(event) => setAllowedTools(event.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {t(
                "adminDashboard.mcps.addDialog.allowedToolsHelp",
                "Comma-separated list of allowed tool names. Leave empty to allow all tools."
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="requireApproval">
              {t("adminDashboard.mcps.addDialog.requireApprovalField", "Require Approval")}
            </Label>
            <Select value={requireApproval} onValueChange={setRequireApproval}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">
                  {t("adminDashboard.mcps.addDialog.approvalNever", "Never")}
                </SelectItem>
                <SelectItem value="always">
                  {t("adminDashboard.mcps.addDialog.approvalAlways", "Always")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              disabled={isPending}
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("adminDashboard.mcps.addDialog.cancel", "Cancel")}
            </Button>
            <Button disabled={isPending} type="submit">
              {isPending ? (
                <>
                  <Spinner />
                  {t("adminDashboard.mcps.addDialog.adding", "Adding...")}
                </>
              ) : (
                t("adminDashboard.mcps.addDialog.add", "Add Server")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
